import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { getImageUrl, getImagePosition } from '../utils/bunnyImage';

// --- MINI-COMPONENT: Handles individual card logic ---
function BreedingBunnyCard({ bunny, onOpenModal }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
      // 1. Check if this specific bunny has an array of images
      if (bunny?.images && bunny.images.length > 1) {
        const imageList = bunny.images;

        // 2. Preload the NEXT image in this bunny's array
        const nextIndex = (currentImageIndex + 1) % imageList.length;
        const nextImg = new Image();
        nextImg.src = getImageUrl(imageList[nextIndex]);

        // 3. Preload the PREVIOUS image
        const prevIndex = (currentImageIndex - 1 + imageList.length) % imageList.length;
        const prevImg = new Image();
        prevImg.src = getImageUrl(imageList[prevIndex]);
      }
      // This tells React to preload every time the user clicks to a new image
    }, [currentImageIndex, bunny.images]);

  const nextImage = (e) => {
    e.stopPropagation();
    if (bunny.images) {
      setCurrentImageIndex((prev) => (prev + 1) % bunny.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (bunny.images) {
      setCurrentImageIndex((prev) => (prev - 1 + bunny.images.length) % bunny.images.length);
    }
  };

   return (
     <div className="bunny-card bunny-profile-card" onClick={() => onOpenModal(bunny)}>
       <div className="card-image-container bunny-profile-image">
         {bunny.images && bunny.images.length > 1 && (
           <button className="carousel-btn left" onClick={prevImage}>&#10094;</button>
         )}

         {bunny.images && bunny.images.length > 0 ? (
           /* JUST the image, no extra divs or background logic */
           <img
             src={getImageUrl(bunny.images[currentImageIndex])}
             alt={bunny.name}
             className="bunny-card-photo"
             loading="lazy"
             style={{ objectPosition: getImagePosition(bunny.images[currentImageIndex]) }}
           />
         ) : (
           <div className="no-image-placeholder">No Image</div>
         )}

         {bunny.images && bunny.images.length > 1 && (
           <button className="carousel-btn right" onClick={nextImage}>&#10095;</button>
         )}

         <div className="bunny-hover-info">
           <h4>{bunny.name}</h4>
         </div>
       </div>

       <h3>{bunny.name}</h3>
       <p className="bunny-card-region">{bunny.region}</p>
       <p className="bunny-card-type">{bunny.type}</p>
     </div>
   );
}

// --- MAIN PAGE COMPONENT ---
function OurBunnies() {
  const [bucks, setBucks] = useState([]);
  const [does, setDoes] = useState([]);
  const [selectedBunny, setSelectedBunny] = useState(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  useEffect(() => {
    const fetchBunnies = async () => {
      const bunnyCollection = collection(db, "bunnies");
      const bunnySnapshot = await getDocs(bunnyCollection);
      const bunnyList = bunnySnapshot.docs.map(doc => ({
        id: doc.id, ...doc.data()
      }));

      // UPDATED FILTER: Must match gender AND be marked as breeding stock
      setBucks(bunnyList.filter(b => b.gender === "Buck" && b.status === "breeding"));
      setDoes(bunnyList.filter(b => b.gender === "Doe" && b.status === "breeding"));
    };
    fetchBunnies();
  }, []);

  const openModal = (bunny) => {
    setSelectedBunny(bunny);
    setModalImageIndex(0);
  };

  const closeModal = () => setSelectedBunny(null);

  const nextModalImage = (e) => {
    e.stopPropagation();
    setModalImageIndex((prev) => (prev + 1) % selectedBunny.images.length);
  };

  const prevModalImage = (e) => {
    e.stopPropagation();
    setModalImageIndex((prev) => (prev - 1 + selectedBunny.images.length) % selectedBunny.images.length);
  };

  return (
    <div className="container">
      <Helmet>
        <title>Our Bunnies | Bunny Boutique 321</title>
        <meta name="description" content="View our amazing bucks and does. These are our current bunnies living with us and having new litters." />
        <link rel="canonical" href="https://bunnyboutique321.com/our-bunnies" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Our Bunnies | Bunny Boutique 321" />
        <meta property="og:description" content="View our amazing bucks and does. These are our current bunnies living with us and having new litters." />
        <meta property="og:url" content="https://bunnyboutique321.com/our-bunnies" />
        <meta property="og:image" content="https://bunnyboutique321.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Bunnies | Bunny Boutique 321" />
        <meta name="twitter:description" content="View our amazing bucks and does. These are our current bunnies living with us and having new litters." />
        <meta name="twitter:image" content="https://bunnyboutique321.com/og-image.jpg" />
      </Helmet>

      <span className="eyebrow">Our Program</span>
      <h1>Meet Our Bunnies</h1>
      <p className="section-subtitle">
        The beautiful foundational bucks and does of Bunny Boutique 321.
      </p>

      {/* --- OUR BUCKS SECTION --- */}
      <h2 style={{ borderBottom: '2px solid #07162c', paddingBottom: '10px', marginTop: '40px', color: '#07162c' }}>Our Bucks</h2>
      {bucks.length === 0 ? (
        <p style={{ color: '#476085' }}>No bucks listed yet.</p>
      ) : (
        <div className="bunny-grid">
          {bucks.map((bunny) => (
            <BreedingBunnyCard key={bunny.id} bunny={bunny} onOpenModal={openModal} />
          ))}
        </div>
      )}

      {/* --- OUR DOES SECTION --- */}
      <h2 style={{ borderBottom: '2px solid #e3bac4', paddingBottom: '10px', marginTop: '60px', color: '#e3bac4' }}>Our Does</h2>
      {does.length === 0 ? (
        <p style={{ color: '#476085' }}>No does listed yet.</p>
      ) : (
        <div className="bunny-grid">
          {does.map((bunny) => (
            <BreedingBunnyCard key={bunny.id} bunny={bunny} onOpenModal={openModal} />
          ))}
        </div>
      )}

      {/* DETAILED MODAL OVERLAY */}
      {selectedBunny && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* MODAL CAROUSEL */}
            <div className="modal-image-container">
              <button className="modal-close-btn" onClick={closeModal} aria-label="Close">&times;</button>

              {selectedBunny.images && selectedBunny.images.length > 1 && (
                <button className="carousel-btn left" onClick={prevModalImage}>&#10094;</button>
              )}

              {selectedBunny.images && selectedBunny.images.length > 0 ? (
                /* Removed the blurred-backdrop div to match the new clean style */
                <img
                  src={getImageUrl(selectedBunny.images[modalImageIndex])}
                  alt={selectedBunny.name}
                  className="foreground-image"
                  loading="eager"
                  fetchpriority="high"
                  style={{ objectPosition: getImagePosition(selectedBunny.images[modalImageIndex]) }}
                />
              ) : (
                <div className="no-image-placeholder">No Image</div>
              )}

              {selectedBunny.images && selectedBunny.images.length > 1 && (
                <button className="carousel-btn right" onClick={nextModalImage}>&#10095;</button>
              )}

              {/* Modal Indicator Dots */}
              {selectedBunny.images && selectedBunny.images.length > 1 && (
                <div style={{ textAlign: 'center', position: 'absolute', bottom: '10px', width: '100%', zIndex: 3 }}>
                  {selectedBunny.images.map((_, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        margin: '0 4px',
                        backgroundColor: modalImageIndex === idx ? '#ffffff' : 'rgba(255,255,255,0.5)',
                        borderRadius: '50%',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.5)'
                      }}
                    ></span>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-body">
              <h3>{selectedBunny.name}</h3>
              <div className="modal-meta-row">
                <span className="modal-tag modal-tag--muted">{selectedBunny.region}</span>
                <span className="modal-tag">{selectedBunny.type}</span>
                <span className="modal-tag modal-tag--muted">{selectedBunny.gender}</span>
              </div>
              <hr />
              <p className="modal-description">{selectedBunny.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OurBunnies;